<template>
    <section>
        <div v-if="liberarAsientoAbonoId || invitacionesAbonoId || cesionAbonoId || cambioAsientoAbonoId || familiasAbonoId" class="tw:flex tw:items-center tw:justify-between tw:mb-6">
            <h2 class="tw:text-xl tw:font-bold tw:text-slate-900 tw:m-0">
                {{ liberarAsientoAbonoId ? $t('perfil.liberar_asiento') : cesionAbonoId ? $t('perfil.ceder_asiento') : cambioAsientoAbonoId ? $t('perfil.cambiar_asiento') : familiasAbonoId ? $t('perfil.familia.header') : $t('perfil.invitaciones') }}
            </h2>
            <Button
                variant="ghost"
                custom-class="tw:font-bold tw:group"
                icon-name="arrow_back"
                icon-name-class="tw:text-lg tw:mr-1 tw:group-hover:-translate-x-1 tw:transition-all tw:duration-300"
                data-testid="liberar-asiento-btn-back"
                @click="onVolver"
            >
                {{ $t('perfil.volver') }}
            </Button>
        </div>

        <!-- Liberar asiento -->
        <LiberarAsientoView
            v-if="liberarAsientoAbonoId"
            :view-mode="viewMode"
            :selected-evento="selectedEvento"
            :eventos="eventos"
            :sesiones="sesiones"
            :loading-eventos="loadingEventos"
            :loading-sesiones="loadingSesiones"
            :error-eventos="errorEventos"
            :error-sesiones="errorSesiones"
            :refund-amount="refundAmount"
            :refund-uses-points="refundUsesPoints"
            :has-eventos="hasEventos"
            :has-sesiones="hasSesiones"
            :is-sesiones-empty="isSesionesEmpty"
            :nombre-abonado="liberarAsientoAbono ? `${liberarAsientoAbono.name ?? ''} ${liberarAsientoAbono.surnames ?? ''}`.trim() : null"
            :n-abonado="liberarAsientoAbono?.n_abonado ?? null"
            @select-evento="selectEvento"
            @open-liberar-modal="openLiberarModal"
        />

        <!-- Flujo inline de invitaciones -->
        <template v-else-if="invitacionesAbonoId">
            <!-- Stepper de generación -->
            <GenerarInvitacionesView
                v-if="generarInvitacionesEvento"
                :abono-id="invitacionesAbonoId"
                :evento-inicial="generarInvitacionesEvento"
                :stats-restantes="invitacionesStatsRestantes"
                :datos-abonado="invitacionesAbono"
                @completado="onStepperCompletado"
                @cancelado="onStepperCancelado"
            />
            <!-- Dashboard de invitaciones -->
            <InvitacionesDashboardView
                v-else
                :abono-id="invitacionesAbonoId"
                :nombre-abonado="invitacionesAbono ? `${invitacionesAbono.name ?? ''} ${invitacionesAbono.surnames ?? ''}`.trim() : null"
                :activo="invitacionesAbono?.activo ?? false"
                :activo-temporada-actual="invitacionesAbono?.activo_temporada_actual ?? false"
                :zona-abonado="invitacionesAbono?.asiento?.region?.nombre ?? invitacionesAbono?.plaza?.region?.nombre ?? invitacionesAbono?.asiento?.zona ?? null"
                :fila-abonado="invitacionesAbono?.asiento?.row ?? invitacionesAbono?.plaza?.fila ?? invitacionesAbono?.asiento?.fila ?? null"
                :asiento-abonado="invitacionesAbono?.asiento?.seat ?? invitacionesAbono?.plaza?.butaca ?? invitacionesAbono?.asiento?.asiento ?? invitacionesAbono?.asiento?.numero ?? null"
                @generar="onGenerarInvitaciones"
            />
        </template>

        <!-- Flujo inline de cambio de asiento (butaca o plaza) -->
        <CambioButacaView
            v-else-if="cambioAsientoAbonoId"
            :abono-id="cambioAsientoAbonoId"
            @completado="onCambioCompletado"
            @cancelado="closeCambioAsiento"
        />

        <!-- Flujo inline de cesión -->
        <CesionInlineView
            v-else-if="cesionAbonoId"
            :abono-id="cesionAbonoId"
            :nombre-abonado="cesionAbono ? `${cesionAbono.name ?? ''} ${cesionAbono.surnames ?? ''}`.trim() : null"
            :n-abonado="cesionAbono?.n_abonado ?? null"
            @close="closeCesionFlow"
        />

        <!-- Flujo inline de familias -->
        <FamiliasView
            v-else-if="familiasAbonoId"
            :abono-id="familiasAbonoId"
            :nombre-abonado="familiasAbono ? `${familiasAbono.name ?? ''} ${familiasAbono.surnames ?? ''}`.trim() : null"
            :n-abonado="familiasAbono?.n_abonado ?? null"
            @close="closeFamilias"
        />

        <template v-else>
            <AbonosList
                :abonos="abonos"
                :loading="loading"
                :error="error"
                :search="search"
                :meta="meta"
                :page="page"
                :per-page="perPage"
                :fetch-abonos="fetchAbonos"
                :desmarcar-renovacion="desmarcarRenovacion"
                :detalle-cache="detalleCache"
                :detalle-loading-ids="detalleLoadingIds"
                :load-detalle="loadDetalle"
                :desmarcar-loading-ids="desmarcarLoadingIds"
                :is-selected="isSelected"
                :selected-plazos="selectedPlazos"
                :selected-count="selectedCount"
                :total-seleccionado="totalSeleccionado"
                :pago-loading="pagoLoading"
                :listas="listas"
                :listas-loading="listasLoading"
                :selected-lista-id="selectedListaId"
                @liberar-asiento="onLiberarAsiento"
                @cambiar-asiento="onCambiarAsiento"
                @ceder-asiento="onCederAsiento"
                @invitaciones="onInvitaciones"
                @ver-familias="onVerFamilias"
                @familia-created="onFamiliaCreated"
                @plazo-toggled="payload => togglePlazo(payload.abonadoId, payload.plazoId, payload.estadoPlazo)"
                @pago-completado="onPagoExternoCompletado"
                @update:search="search = $event"
                @update:lista-id="onListaFilterChange"
            />
        </template>

        <!-- Modal de confirmación de liberar asiento -->
        <BaseModalFront
            :show="showLiberarModal"
            :title="$t('perfil.asiento_liberado_confirmacion')"
            :message="$t('perfil.asiento_liberado_terminos')"
            :confirm-text="$t('perfil.asiento_liberado_SI')"
            :cancel-text="$t('perfil.asiento_liberado_NO')"
            confirm-variant="danger"
            data-testid="liberar-asiento-confirm-modal"
            @close="closeLiberarModal"
            @confirm="handleLiberarAsiento"
        />
    </section>
</template>

<script setup>
    import { ref, onMounted, onActivated } from 'vue';
    import AbonosList from '@/modules/front/perfil/components/abonos/List.vue';
    import BaseModalFront from '@/modules/front/ui/BaseModalFront.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import { useAbonos } from '@/composables/front/perfil/useAbonos';
    import { useLiberarAsiento } from '@/composables/front/perfil/useLiberarAsiento';
    import { useRoute, useRouter } from '@/composables/common/router/useRouter';
    import { useToast } from '@/composables/common/useToast';
    import { t } from '@/utils/i18n-helper';
    import LiberarAsientoView from './LiberarAsientoView.vue';
    import InvitacionesDashboardView from './InvitacionesDashboardView.vue';
    import GenerarInvitacionesView from './GenerarInvitacionesView.vue';
    import CambioButacaView from './CambioButacaView.vue';
    import CesionInlineView from './CesionInlineView.vue';
    import FamiliasView from './PerfilFamiliaView.vue';

    const {
        abonos,
        loading,
        error,
        search,
        meta,
        page,
        perPage,
        fetchAbonos,
        invalidateAndReload,
        desmarcarRenovacion,
        detalleCache,
        detalleLoadingIds,
        loadDetalle,
        desmarcarLoadingIds,
        togglePlazo,
        isSelected,
        selectedPlazos,
        selectedCount,
        totalSeleccionado,
        pagoLoading,
        onPagoExternoCompletado,
        listas,
        listasLoading,
        selectedListaId,
        onListaFilterChange,
    } = useAbonos();

    const toast = useToast();
    const route = useRoute();
    const router = useRouter();

    onMounted(() => {
        fetchAbonos();
        mostrarToastCambioButacaDesdeQuery();
    });
    onActivated(() => { fetchAbonos(); });

    /**
     * Cuando se entra en `/perfil?tab=abonos&cambio-butaca=true` (redirección
     * de la pasarela tras pagar un cambio de butaca), muestra el toast de
     * éxito y limpia el query para que no se repita al recargar la página.
     * Solo actúa en montajes frescos: en el flujo inline el toast ya lo
     * muestra `onCambioCompletado` vía el evento `completado`.
     */
    const mostrarToastCambioButacaDesdeQuery = () => {
        if (!route || route.query?.['cambio-butaca'] !== 'true') return;
        toast.success(t('perfil.cambio_asiento.butaca_cambiada_ok'));
        router.replace({ query: { ...route.query, 'cambio-butaca': undefined } });
    };

    // ─── Liberar asiento ────────────────────────────────────────────────────
    const liberarAsientoAbonoId = ref(null);
    const liberarAsientoAbono = ref(null);
    const cesionAbonoId = ref(null);
    const cesionAbono = ref(null);
    const cambioAsientoAbonoId = ref(null);

    const onLiberarAsiento = abonoId => {
        liberarAsientoAbonoId.value = abonoId;
        // Priorizar el detalle (tiene info completa) sobre el listado
        liberarAsientoAbono.value = detalleCache.value.get(abonoId)
            ?? abonos.value.find(a => a.id === abonoId)
            ?? null;
    };

    const onCederAsiento = abonoId => {
        cesionAbonoId.value = abonoId;
        cesionAbono.value = detalleCache.value.get(abonoId)
            ?? abonos.value.find(a => a.id === abonoId)
            ?? null;
    };

    const closeCesionFlow = () => {
        cesionAbonoId.value = null;
        cesionAbono.value = null;
    };

    const onCambiarAsiento = abonoId => {
        cambioAsientoAbonoId.value = abonoId;
    };

    const closeCambioAsiento = () => {
        cambioAsientoAbonoId.value = null;
    };

    const onCambioCompletado = () => {
        toast.success(t('perfil.cambio_asiento.completado'));
        const abonoId = cambioAsientoAbonoId.value;
        cambioAsientoAbonoId.value = null;
        if (abonoId) invalidateAndReload(abonoId);
        fetchAbonos();
    };

    const closeLiberarAsiento = () => {
        liberarAsientoAbonoId.value = null;
        liberarAsientoAbono.value = null;
    };

    const onVolver = () => {
        if (liberarAsientoAbonoId.value) return goBack();
        if (generarInvitacionesEvento.value) return onStepperCancelado();
        if (cambioAsientoAbonoId.value) return closeCambioAsiento();
        if (cesionAbonoId.value) return closeCesionFlow();
        if (familiasAbonoId.value) return closeFamilias();
        closeInvitaciones();
    };

    const {
        viewMode,
        selectedEvento,
        eventos,
        sesiones,
        loadingEventos,
        loadingSesiones,
        errorEventos,
        errorSesiones,
        showLiberarModal,
        hasEventos,
        hasSesiones,
        isSesionesEmpty,
        refundAmount,
        refundUsesPoints,
        selectEvento,
        goBack,
        openLiberarModal,
        closeLiberarModal,
        handleLiberarAsiento,
    } = useLiberarAsiento(liberarAsientoAbonoId, closeLiberarAsiento);

    const invitacionesAbonoId = ref(null);
    const invitacionesAbono = ref(null);
    const generarInvitacionesEvento = ref(null);
    const invitacionesStatsRestantes = ref(null);

    const onInvitaciones = async abonoId => {
        invitacionesAbonoId.value = abonoId;
        generarInvitacionesEvento.value = null;

        // Priorizar el detalle (tiene zona/fila/asiento) sobre el listado
        if (!detalleCache.value.has(abonoId)) {
            await loadDetalle(abonoId);
        }
        invitacionesAbono.value = detalleCache.value.get(abonoId)
            ?? abonos.value.find(a => a.id === abonoId)
            ?? null;
    };

    const closeInvitaciones = () => {
        invitacionesAbonoId.value = null;
        invitacionesAbono.value = null;
        generarInvitacionesEvento.value = null;
        invitacionesStatsRestantes.value = null;
    };

    const onGenerarInvitaciones = ({ evento, statsRestantes }) => {
        generarInvitacionesEvento.value = evento;
        invitacionesStatsRestantes.value = statsRestantes ?? null;
    };

    const onStepperCompletado = ({ session_id, n_pedido }) => {
        if (session_id && n_pedido) {
            window.location.href = `/compra/${session_id}/${n_pedido}`;
        } else {
            generarInvitacionesEvento.value = null;
        }
    };

    const onStepperCancelado = () => {
        generarInvitacionesEvento.value = null;
        invitacionesStatsRestantes.value = null;
    };

    // ─── Familias ───────────────────────────────────────────────────────────
    const familiasAbonoId = ref(null);
    const familiasAbono = ref(null);

    const onFamiliaCreated = abonadoId => {
        // Invalidar cache del detalle para que loadDetalle refresque tiene_familia
        const nextCache = new Map(detalleCache.value);
        nextCache.delete(abonadoId);
        detalleCache.value = nextCache;
        loadDetalle(abonadoId);
    };

    const onVerFamilias = async abonoId => {
        familiasAbonoId.value = abonoId;
        window.scrollTo(0, 0);

        // Priorizar el detalle (tiene info completa) sobre el listado
        if (!detalleCache.value.has(abonoId)) {
            await loadDetalle(abonoId);
        }
        familiasAbono.value = detalleCache.value.get(abonoId)
            ?? abonos.value.find(a => a.id === abonoId)
            ?? null;
    };

    const closeFamilias = async() => {
        const abonoId = familiasAbonoId.value;
        familiasAbonoId.value = null;
        familiasAbono.value = null;
        if (abonoId) {
            // Invalidar TODO el detalle cache para que todos los abonos
            // (incluido el que se agregó como miembro) refresquen tiene_familia
            detalleCache.value = new Map();
            // Refrescar la lista y el detalle del abono actual
            await Promise.all([
                fetchAbonos(),
                loadDetalle(abonoId),
            ]);
        }
    };
</script>
